import './detailsstep.less'

import * as Sentry from '@sentry/react'
import {Form, Formik, FormikErrors, useFormikContext} from 'formik'
import requestService from 'mithril/request/request'
import {RegistrationModel} from 'models/registration/RegistrationModel'
import {StepKey} from 'models/registration/StepModel'
import {
	DetailsInfoState,
	DetailsStepModel
} from 'models/registration/steps/DetailsStepModel'
import React, {useEffect, useState} from 'react'
import {useHistory} from 'react-router'
import {FormikChanges} from 'util/formikchanges'
import {t} from 'util/i18n'
import {style} from 'util/style'
import {FormField, FormSelect} from 'views/form/form'
import {Info} from 'views/layout/info'
import {PopupModal} from 'views/layout/popupmodal'
import {Step} from 'views/registration/step'
import {Theme} from 'views/theme'

const request = requestService(window, Promise).request

const styles = style('detailsstep', ['popup'])

export const DetailsStep: React.FC<{
	step: StepKey
	registration: RegistrationModel
	updateRegistration: (model: RegistrationModel) => void
}> = attrs => {
	const {registration, updateRegistration} = attrs
	const step = registration.allSteps.details
	const field = {step}
	const history = useHistory()

	const handleSubmit = async (
		values: DetailsInfoState,
		setErrors: (errors: FormikErrors<DetailsInfoState>) => void
	) => {
		const {email} = values
		if (email) Sentry.setUser({email})
		let newRegistration = registration
		if (!registration.state.validated) {
			const response = await request({
				method: 'POST',
				url: `/api/register/${registration.eventID}/check-email`,
				data: {email: step.email}
			})
			newRegistration = step.validateEmail(step.email, response.status)
		}

		newRegistration = newRegistration.allSteps.details.setDetails(values)
		const newStep = newRegistration.allSteps.details
		updateRegistration(newRegistration)

		request({
			method: 'POST',
			url: `/api/register/${newRegistration.id}/check-details`,
			data: newStep.state
		})
			.then(response => {
				history.push(`#${newStep.nextStepKey}`)
			})
			.catch(error => {
				console.error(JSON.parse(error.message))
				setErrors(JSON.parse(error.message))
			})
	}

	return (
		<Formik<DetailsInfoState>
			initialValues={step.state}
			validate={values => step.validate(values)}
			onSubmit={(values, {setErrors}) => handleSubmit(values, setErrors)}
		>
			<Form>
				<DetailsStepPopup step={step} />
				<FormikChanges
					onChange={({values}) => {
						const newValues = clearFields(step, values)
						const newRegistration = step.setDetails({...newValues})
						updateRegistration(newRegistration)
					}}
				/>
				<Step step={step} hasFormSubmit={true}>
					<Theme.Wysiwyg {...styles('intro')}>
						{step.texts.personal}
					</Theme.Wysiwyg>
					<DetailsField {...field} name="firstname" />
					<DetailsField {...field} name="lastname" />
					<DetailsField
						{...field}
						name="email"
						type="email"
						autoComplete="email"
						onChange={() => {
							const newRegistration = step.invalidateEmail()
							updateRegistration(newRegistration)
						}}
						onBlur={() => {
							request({
								method: 'POST',
								url: `/api/register/${registration.eventID}/check-email`,
								data: {email: step.email}
							}).then(response => {
								const newRegistration = step.validateEmail(
									step.email,
									response.status
								)
								updateRegistration(newRegistration)
							})
						}}
					/>
					<Theme.Wysiwyg {...styles('intro')}>
						{step.texts.company}
					</Theme.Wysiwyg>
					{step.shouldShowAffiliatedFields && (
						<DetailsCheckbox
							name="not_affiliated"
							label={t('details.not_affiliated')}
						/>
					)}
					{step.shouldShowCompanyFields && (
						<>
							<DetailsField {...field} name="function" />
							<DetailsField {...field} name="company" />
							{step.registration.tours.length > 0 && (
								<DetailsCheckbox
									name="company_belgian"
									label={
										<span>
											{t('details.company_belgian')}{' '}
											<Info
												label={t('details.company_belgian_popup_label')}
												popup={t('details.company_belgian_popup')}
											/>
										</span>
									}
								/>
							)}
							{step.shouldShowKPInputField && (
								<DetailsField
									{...field}
									name="kp"
									help={t('details.kp_help')}
								/>
							)}
							{
								<DetailsSelect
									{...field}
									name="industry"
									groups={t('details.industries').map(label => ({
										label,
										value: label
									}))}
								/>
							}
							{step.shouldDescribeIndustry && (
								<DetailsField
									{...field}
									name="industry_description"
									required={true}
								/>
							)}
						</>
					)}
					<DetailsSelect
						{...field}
						name="country"
						groups={Object.keys(registration.countries).map(key => ({
							value: registration.countries[key],
							label: key
						}))}
					/>
					{step.shouldShowCompanyFields && (
						<DetailsField {...field} name="phone" />
					)}
					{step.shouldShowVATFields && (
						<DetailsField {...field} name="vat" help={t('details.vat_help')} />
					)}
					{step.shouldShowAddressFields && (
						<>
							<DetailsField {...field} name="street" />
							<DetailsField {...field} name="number" />
							<DetailsField {...field} name="box" />
							<DetailsField {...field} name="postal_code" />
							<DetailsField {...field} name="city" />
							<DetailsField {...field} name="state" />
						</>
					)}
					<br />
					{step.shouldShowInvoiceAddressFields && (
						<DetailsCheckbox
							name="other_invoice_address"
							label={t('details.other_invoice_address')}
						/>
					)}
					{step.shouldShowInvoiceAddressFields &&
						step.state.other_invoice_address && (
							<>
								<DetailsField {...field} name="invoice_address" />
								<DetailsField {...field} name="invoice_address_line2" />
								<DetailsField {...field} name="invoice_postal_code" />
								<DetailsField {...field} name="invoice_city" />
								<DetailsField {...field} name="invoice_state" />
								<DetailsSelect
									{...field}
									name="invoice_country"
									groups={Object.keys(registration.countries).map(key => ({
										value: registration.countries[key],
										label: key
									}))}
								/>
								<DetailsField {...field} name="invoice_vat" />
							</>
						)}
				</Step>
			</Form>
		</Formik>
	)
}

export const DetailsStepPopup: React.FC<{
	step: DetailsStepModel
}> = ({step}) => {
	const [showPopup, setShowPopup] = useState(false)
	const registration = step.registration
	const status = registration.status
	const {validateForm} = useFormikContext()

	useEffect(() => {
		if (status.type !== 'regular') {
			setShowPopup(true)
		}
		validateForm()
	}, [status.type])

	const getHtml = () => {
		switch (status.type) {
			case 'registered':
				return step.config.already_registered_text
			case 'invitee':
				return status.imec
					? step.config.free_imec_text
					: status.free_options
					? step.config.free_text
					: step.config.free_with_paid_options_text
			default:
				return ''
		}
	}

	return (
		<PopupModal
			open={showPopup}
			onClose={() => setShowPopup(false)}
			{...styles.popup}
		>
			<PopupModal.Block>
				<PopupModal.Content mod="big">
					<Theme.H1 as="h2" mod={['dots']}>
						{status.type === 'registered'
							? step.config.already_registered_title
							: step.config.free_title}
					</Theme.H1>
					<PopupModal.Message mod="fullwidth">
						<Theme.Wysiwyg>{getHtml()}</Theme.Wysiwyg>
					</PopupModal.Message>
				</PopupModal.Content>
				<PopupModal.Footer />
			</PopupModal.Block>
		</PopupModal>
	)
}

export const DetailsSelect: React.FC<{
	step: DetailsStepModel
	name: keyof DetailsInfoState
	groups: Array<{
		label: string
		value: string
	}>
}> = ({step, groups, name}) => {
	return (
		<FormSelect
			name={name}
			required={step.getIsRequiredField(name)}
			label={t(`details.${name}`)}
			groups={groups}
		/>
	)
}

export const DetailsField: React.FC<{
	step: DetailsStepModel
	name: keyof DetailsInfoState
	help?: string
	type?: 'email'
} & Record<string, any>> = ({help, step, name, ...props}) => {
	return (
		<FormField
			name={name}
			required={step.getIsRequiredField(name)}
			label={t(`details.${name}`)}
			help={help}
			{...props}
		/>
	)
}

export const DetailsCheckbox: React.FC<{
	name: keyof DetailsInfoState
	label: any
}> = ({name, label}) => {
	return <FormField type="checkbox" name={name} label={label} />
}

const clearFields = (
	step: DetailsStepModel,
	values: Partial<DetailsInfoState>
) => {
	if (step.shouldShowKPInputField) {
		clearVATFields(values)
	}
	if (!step.shouldShowCompanyFields) {
		clearCompanyFields(values)
	}
	if (!step.shouldShowKPInputField) {
		clearKPFields(values)
	}
	if (!step.shouldShowAddressFields) {
		clearAddressFields(values)
	}
	if (!values.other_invoice_address) {
		clearInvoiceAddressFields(values)
	}

	if (!step.shouldShowAffiliatedFields) {
		clearNotAffiliatedFields(values)
	}
	return values
}

const clearCompanyFields = (values: Partial<DetailsInfoState>) => {
	const newValues = values
	delete newValues.function
	delete newValues.company
	delete newValues.company_belgian
	delete newValues.kp
	delete newValues.industry
	delete newValues.industry_description
	delete newValues.phone
	delete newValues.vat
	return newValues
}
const clearKPFields = (values: Partial<DetailsInfoState>) => {
	const newValues = values
	delete newValues.kp
	return newValues
}
const clearVATFields = (values: Partial<DetailsInfoState>) => {
	const newValues = values
	delete newValues.vat
	return newValues
}
const clearAddressFields = (values: Partial<DetailsInfoState>) => {
	const newValues = values
	delete newValues.street
	delete newValues.number
	delete newValues.box
	delete newValues.postal_code
	delete newValues.city
	delete newValues.state
	delete newValues.other_invoice_address
	return newValues
}
const clearInvoiceAddressFields = (values: Partial<DetailsInfoState>) => {
	const newValues = values
	delete newValues.invoice_address
	delete newValues.invoice_address_line2
	delete newValues.invoice_postal_code
	delete newValues.invoice_city
	delete newValues.invoice_state
	delete newValues.invoice_country
	delete newValues.invoice_vat
	delete newValues.other_invoice_address
	return newValues
}
const clearNotAffiliatedFields = (values: Partial<DetailsInfoState>) => {
	const newValues = values
	delete newValues.not_affiliated
	return newValues
}
